import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CreatingAccount: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Getting Started
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Creating Your Account
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Setting up an IndeeClips account is quick and free. This guide walks you through registration, email verification, and creating your profile to start watching and uploading videos.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Step 1: Visit the Sign-Up Page
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Go to indeeclips.com</li>
          <li>Click "Sign Up" in the top-right corner</li>
          <li>You'll be directed to the registration page</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Step 2: Enter Your Information
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Fill in the registration form with the following details:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Full Name:</strong> Your real name or display name (can be changed later)</li>
          <li><strong>Email Address:</strong> Use a valid email you can access for verification</li>
          <li><strong>Password:</strong> Must be at least 8 characters with letters and numbers</li>
          <li><strong>Date of Birth:</strong> Required to ensure age-appropriate content (18+ recommended)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Step 3: Agree to Terms and Create Account
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Read the Terms of Service and Privacy Policy (links provided)</li>
          <li>Check the box to agree to the terms</li>
          <li>Click "Create Account"</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Step 4: Verify Your Email
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          After creating your account:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Check your email inbox for a verification message from IndeeClips</li>
          <li>Open the email and click the verification link</li>
          <li>You'll be redirected to IndeeClips with a confirmation message</li>
          <li>Your account is now verified and active</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          <strong>Didn't receive the email?</strong> Check your spam folder. If it's not there, click "Resend Verification Email" on your profile page.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Step 5: Set Up Your Profile (Optional)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Once logged in, customize your profile:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Profile Picture:</strong> Upload a photo or avatar (JPG/PNG, max 2MB)</li>
          <li><strong>Bio:</strong> Add a short description about yourself</li>
          <li><strong>Language Preference:</strong> Choose Hindi or English as your interface language</li>
          <li><strong>Theme:</strong> Select Light, Dark, or System Default mode</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Account Features You Get
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Subscribe to channels and get notifications</li>
          <li>Like, comment, and share videos</li>
          <li>Create playlists and save videos for later</li>
          <li>View personalized recommendations</li>
          <li>Upload your own videos and clips (requires channel creation)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Troubleshooting Common Issues
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          "Email already exists"
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          This email is already registered. Try logging in instead, or use the "Forgot Password" option.
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          "Verification link expired"
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Verification links expire after 24 hours. Request a new verification email from your profile page.
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          "Invalid email format"
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Ensure your email follows the format: username@domain.com with no spaces or special characters.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Security Tips
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Use a strong password:</strong> Mix uppercase, lowercase, numbers, and symbols</li>
          <li><strong>Don't share your password:</strong> IndeeClips will never ask for it via email</li>
          <li><strong>Enable two-factor authentication:</strong> Coming soon for extra security</li>
          <li><strong>Keep email access secure:</strong> Your email is used for password recovery</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Need More Help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Having trouble creating your account? Contact support at{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default CreatingAccount;
